// The one place that knows how a profile's hot takes are stored. Everything
// that shows a hot take reads through hotTakesOf(), and the Hot Take block
// saves through hotTakesPatch().
//
// Two columns, because the list arrived after the single take
// (20261008100000_hot_takes_list.sql):
//   hot_takes  text[], the list in slide order
//   hot_take   the old single take, read only while hot_takes is NULL
// NULL and an empty list mean opposite things. NULL: this reader has not
// saved since the change, so their old hot_take (if any) is slide 1. Empty:
// they deleted every take, and the old one must NOT come back. Every save
// writes the list and clears hot_take, so that can never happen.

// Both must match user_profiles_hot_takes_check (hot_takes_are_valid(),
// 20261008100000_hot_takes_list.sql: cardinality <= 4, char_length <= 300).
// If they drift, the browser lets through a list the database refuses, and
// the reader sees a failed save instead of a sentence telling them why.
export const MAX_HOT_TAKES = 4;
export const HOT_TAKE_MAX_LENGTH = 300;

const isTake = t => typeof t === 'string' && t.trim() !== '';

// The list to show, in slide order. Never null.
export function hotTakesOf(profile) {
  if (Array.isArray(profile?.hot_takes)) return profile.hot_takes.filter(isTake);
  return isTake(profile?.hot_take) ? [profile.hot_take] : [];
}

// Slide 1, which is what the share image and the community feed show.
export function firstHotTake(profile) {
  return hotTakesOf(profile)[0] || null;
}

// What actually gets stored: each take trimmed, blanks dropped. The database
// refuses a blank entry, so one is never sent.
export function cleanHotTakes(list) {
  return (list || []).filter(isTake).map(t => t.trim());
}

// Why this list cannot be saved, in words for the reader, or null if it can.
// The same rules as the database, checked first so a refusal is never the
// first a reader hears of them.
export function hotTakesProblem(list) {
  const clean = cleanHotTakes(list);
  if (clean.length > MAX_HOT_TAKES) return `You can have up to ${MAX_HOT_TAKES} hot takes.`;
  const tooLong = clean.findIndex(t => t.length > HOT_TAKE_MAX_LENGTH);
  if (tooLong !== -1) {
    return `Hot take ${tooLong + 1} is ${clean[tooLong].length} characters. Shorten it to ${HOT_TAKE_MAX_LENGTH} to save.`;
  }
  return null;
}

// The update to send for a list. hot_take is cleared in the same write so
// the old single take cannot reappear once the list is the record.
export function hotTakesPatch(list) {
  return { hot_takes: cleanHotTakes(list), hot_take: null };
}
